/* eslint-disable react/display-name */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { MdOutlineTask } from 'react-icons/md';
import { Box, Center, Spinner, Stack, Text } from '@chakra-ui/core';
import { useStoreState } from '../../../models/hooks';
import { FormattedTodo } from '../types';
import { VirtualTable } from '../../../app/components/VirtualTable';
import { ActionTypes } from './cells/ActionTypes';
import { CandidateName } from './cells/CandidateName';
import { LastUpdate } from './cells/LastUpdate';
import { LocationCell } from './cells/Location';
import useLookupsByType from '../../../app/hooks/useLookupsByType';
import useContactRequestDeclineReasons from '../../messaging/seeker/candidate/history/useContactRequestDeclinedReasons';
import { TodosType } from '../../../firebase/firestore/documents/lookups';
import { TodoPagination } from './TodoPagination';
import { Status } from './cells/Status';
import { RecruiterToDoStatusFilter } from '../RecruiterToDoStatusFilter';
import { ActionModals } from '../ActionModals';
import colors from '../../../styles/colors';

interface TodoTableProps {
  candidateTodos: TodosType | undefined;
  declinedReasonsTypes: ReturnType<typeof useContactRequestDeclineReasons>;
  offerRejectedReasons: ReturnType<typeof useLookupsByType>;
}

export const TodoTable = React.memo(function TodoTable({
  candidateTodos,
  declinedReasonsTypes,
  offerRejectedReasons,
}: TodoTableProps) {
  const { t } = useTranslation();

  const { isLoading, paginatedTodos } = useStoreState((state) => state.todo);

  const headerInfoFromTranslation = {
    action: t('toDoList:actionToTake'),
    lastStatus: t('toDoList:lastStatus'),
    status: t('toDoList:status'),
    locationAndPosition: t('toDoList:locationAndPosition'),
    candidateName: t('toDoList:candidate'),
  };

  const columns = React.useMemo<Column<FormattedTodo>[]>(
    () => [
      {
        Header: headerInfoFromTranslation.action,
        accessor: 'actionType',
        // width: 'minmax(0, 1fr)',
        id: 'todo',
        Cell: ({ cell }) => (
          <ActionTypes
            cellData={cell}
            candidateTodos={candidateTodos}
            declinedReasonsTypes={declinedReasonsTypes}
            offerRejectedReasons={offerRejectedReasons}
            toDos={paginatedTodos.data}
          />
        ),
        disableSortBy: true,
      },
      {
        Header: headerInfoFromTranslation.lastStatus,
        accessor: 'lastStatus',
        id: 'last update',
        width: 70,
        Cell: ({ cell }) => <LastUpdate cellData={cell} candidateTodos={candidateTodos} />,
        disableSortBy: true,
      },
      {
        Header: () => <RecruiterToDoStatusFilter onCompletedFilterChange={() => {}} />,
        accessor: 'status',
        id: 'status',
        maxWidth: 40,
        minWidth: 40,
        Cell: ({ cell }) => <Status cellData={cell} />,
        disableSortBy: true,
      },
      {
        Header: headerInfoFromTranslation.locationAndPosition,
        accessor: 'location',
        id: 'location',
        width: 40,
        Cell: ({ cell }) => <LocationCell cellData={cell} />,
        disableSortBy: true,
      },
      {
        Header: headerInfoFromTranslation.candidateName,
        accessor: 'candidateFullName',
        id: 'candidate name',
        width: 40,
        Cell: ({ cell }) => <CandidateName cellData={cell} />,
        disableSortBy: true,
      },
    ],
    [
      headerInfoFromTranslation.action,
      headerInfoFromTranslation.lastStatus,
      headerInfoFromTranslation.locationAndPosition,
      headerInfoFromTranslation.candidateName,
      candidateTodos,
      declinedReasonsTypes,
      offerRejectedReasons,
      paginatedTodos.data,
    ],
  );
  if (isLoading)
    return (
      <Center flex={1} height="100vh">
        <Stack align="center" spacing={2}>
          <Spinner />
          <Text>{t('toDoList:todoTableLoaderText')}</Text>
        </Stack>
      </Center>
    );

  return (
    <Stack flex={1} spacing={0} p={3} pt={0}>
      <VirtualTable
        data={paginatedTodos.data || []}
        getDataId={(data) => data.id}
        columns={columns}
        rowHeight={100}
        tableHeight="calc(100vh - 148px)"
        pagination={<TodoPagination />}
        emptyTable={
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={10} height="100%">
            <MdOutlineTask size={104} color={colors.gray[400]} />
            <Text py={3} fontSize="24px" fontWeight={700}>
              {t('toDoList:allCaughtUp')}
            </Text>
            <Text fontSize="16px" fontWeight={400} textAlign="center" color="#718096">
              {t('toDoList:noPendingUpdates')}
              <br />
              {t('toDoList:checkBackLater')}
            </Text>
          </Box>
        }
      />
      <ActionModals candidateTodos={candidateTodos} />
    </Stack>
  );
});
